import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { cloneDeep, isEmpty, uniqBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useGrowthMarginDriversContext } from '../context/GrowthMarginDriversContext';
import { getWaterFallChartData } from './growthMarginDataUtils';

const useProfitGrowthDriversData = () => {
    const { selectedSubFilter, selectedGrowthMarginDriver } = useGrowthMarginDriversContext();

    const barKpiKey = selectedGrowthMarginDriver?.growthKey || selectedGrowthMarginDriver?.key;
    // A year ago KPI reads the ya_ columns of the selected period, the same basis as the bars, so the
    // period must not also be shifted back a year.
    const yearAgoKpiKey = selectedGrowthMarginDriver?.yearAgoKey;
    const previousYearKpiKey = yearAgoKpiKey || selectedGrowthMarginDriver?.key;

    const [profitGrowthDriversData, setProfitGrowthDriversData] = useState<any[]>([]);
    const [min, setMin] = useState<number>(0);
    const [max, setMax] = useState<number>(0);

    // Get Previous year Data
    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    const {
        data: previousYearData,
        isPending: isPreviousYearDataPending,
        isError: isPreviousYearDataError,
        isSuccess: isPreviousYearDataSuccess,
    } = useQuery({
        queryKey: [
            'previous-year-profit-growth-drivers-data',
            selectedSubFilter,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
            selectedGrowthMarginDriver,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                limit: PROFIT_POOL_API_LIMIT,
                offset: 0,
                kpi: previousYearKpiKey,
                tierList: singleSelectedTier,
                yearAgo: !yearAgoKpiKey,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    // Get Current year Data
    const {
        data: currentYearProfitData,
        isPending: isCurrentYearProfitPending,
        isError: isCurrentYearProfitError,
        isSuccess: isCurrentYearProfitSuccess,
    } = useQuery({
        queryKey: [
            'current-year-profit-growth-drivers-data',
            selectedSubFilter,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
            selectedGrowthMarginDriver,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                limit: PROFIT_POOL_API_LIMIT,
                offset: 0,
                kpi: selectedGrowthMarginDriver?.key,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: [],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                signal,
            });
        },
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    // Get WaterFallChartData

    const {
        data: attributeProfitData,
        isPending: isAttributeProfitPending,
        isError: isAttributeProfitError,
        isSuccess: isAttributeProfitSuccess,
        fetchNextPage: fetchAttributeProfitNextPage,
        hasNextPage: hasNextAttributeProfitPage,
        isFetchingNextPage: isFetchingAttributeProfitNextPage,
    } = useInfiniteQuery({
        queryKey: [
            'profit-growth-drivers-infi',
            selectedSubFilter,
            barKpiKey,
            singleSelectedTier,
            idForRestOfComponent,
            timePeriodSelection,
        ],
        queryFn: ({ pageParam, signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter,
                limit: PROFIT_POOL_API_LIMIT,
                offset: pageParam as number,
                kpi: barKpiKey,
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                absValueSortOrder: 'desc',
                signal,
            });
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            return lastPageParam + PROFIT_POOL_API_LIMIT;
        },
        initialPageParam: 0,
        enabled: Boolean(
            allDimAndAttributeTypeList &&
                'loading' in allDimAndAttributeTypeList &&
                !timePeriodLoading &&
                timePeriodSelection,
        ),
    });

    useEffect(() => {
        if (!isAttributeProfitPending && attributeProfitData?.pages) {
            const allData = attributeProfitData.pages.flatMap((page: any) => page?.data || []);

            if (!isEmpty(allData)) {
                let minProfit = Number.POSITIVE_INFINITY;
                let maxProfit = Number.NEGATIVE_INFINITY;

                const finalChartData = allData.map((item: any) => {
                    const currentProfit = item.kpi_values?.[barKpiKey] || 0;
                    if (currentProfit < minProfit) minProfit = currentProfit;
                    if (currentProfit > maxProfit) maxProfit = currentProfit;
                    return {
                        attribute_ex_id: item.attribute_ex_id,
                        attributeDesc: item.attribute_desc,
                        [selectedGrowthMarginDriver?.key]: currentProfit,
                        tier: item.tier,
                    };
                });

                setMin(minProfit === Number.POSITIVE_INFINITY ? 0 : minProfit);
                setMax(maxProfit === Number.NEGATIVE_INFINITY ? 0 : maxProfit);

                setProfitGrowthDriversData(uniqBy(finalChartData, 'attribute_ex_id'));
            } else {
                setProfitGrowthDriversData([]);
                setMin(0);
                setMax(0);
            }
        }
    }, [isAttributeProfitPending, attributeProfitData]);

    // Memoize waterfall chart data to avoid recalculating multiple times
    const waterfallChartResult = useMemo(() => {
        return getWaterFallChartData(
            profitGrowthDriversData,
            previousYearData?.data?.[0]?.kpi_values?.[previousYearKpiKey] || 0,
            currentYearProfitData?.data?.[0]?.kpi_values?.[selectedGrowthMarginDriver?.key] || 0,
            selectedGrowthMarginDriver?.key,
        );
    }, [
        profitGrowthDriversData,
        previousYearData,
        currentYearProfitData,
        selectedGrowthMarginDriver?.key,
        previousYearKpiKey,
    ]);

    return {
        data: profitGrowthDriversData,
        currentYearData: currentYearProfitData?.data?.[0]?.kpi_values?.[selectedGrowthMarginDriver?.key] || 0,
        previousYearData: previousYearData?.data?.[0]?.kpi_values?.[previousYearKpiKey] || 0,
        waterFallChartData: waterfallChartResult?.chartData,
        min: waterfallChartResult?.min,
        max: waterfallChartResult?.max,
        isPending: isPreviousYearDataPending || isCurrentYearProfitPending || isAttributeProfitPending,
        isError: isPreviousYearDataError || isCurrentYearProfitError || isAttributeProfitError,
        isSuccess: isPreviousYearDataSuccess && isCurrentYearProfitSuccess && isAttributeProfitSuccess,
        fetchNextPage: fetchAttributeProfitNextPage,
        hasNextPage: hasNextAttributeProfitPage,
        isFetchingNextPage: isFetchingAttributeProfitNextPage,
    };
};

export default useProfitGrowthDriversData;
